import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import PersonalDataSection from '../components/membership/PersonalDataSection';
import CompanyDataSection from '../components/membership/CompanyDataSection';
import LegalRepresentativeSection from '../components/membership/LegalRepresentativeSection';
import DeclarationSection from '../components/membership/DeclarationSection';
import DocumentsUploadSection from '../components/membership/DocumentsUploadSection';
import PrivacyConsentsSection from '../components/membership/PrivacyConsentsSection';
import LoadingButton from '../components/LoadingButton';

const EditMembershipPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validToken, setValidToken] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState({
    // Dati personali
    firstName: '',
    lastName: '',
    birthPlace: '',
    birthProvince: '',
    birthDate: '',
    role: '',
    // Dati azienda
    businessName: '',
    vatNumber: '',
    fiscalCode: '',
    street: '',
    number: '',
    city: '',
    province: '',
    zipCode: '',
    phone: '',
    fax: '',
    mobile: '',
    email: '',
    website: '',
    pec: '',
    iban: '',
    mainActivity: '',
    // Rappresentante legale
    hasRepresentative: false,
    repFirstName: '',
    repLastName: '',
    repBirthPlace: '',
    repBirthProvince: '',
    repBirthDate: '',
    repPosition: '',
    repMobile: ''
  });

  const [documents, setDocuments] = useState({
    identity: null as File | null,
    authorization: null as File | null,
    businessRegistry: null as File | null,
    representativeDocument: null as File | null
  });

  const [existingDocuments, setExistingDocuments] = useState({
    identity: null as { filename: string; originalName: string } | null,
    authorization: null as { filename: string; originalName: string } | null,
    businessRegistry: null as { filename: string; originalName: string } | null,
    representativeDocument: null as { filename: string; originalName: string } | null
  });

  const [declarations, setDeclarations] = useState({
    acceptsAssociationPolicy: false,
    acceptsProfessionalConduct: false
  });

  const [consents, setConsents] = useState({
    dataProcessing: false,
    mandatory: false,
    promotionalTraditional: false,
    promotionalElectronic: false
  });

  useEffect(() => {
    fetchMembershipData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [token]);

  const fetchMembershipData = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/memberships/edit/${token}`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Link non valido o scaduto');
      }

      const membership = data.data;
      setValidToken(true);

      // Popola il form con i dati esistenti
      setFormData({
        firstName: membership.personalData.firstName,
        lastName: membership.personalData.lastName,
        birthPlace: membership.personalData.birthPlace,
        birthProvince: membership.personalData.birthProvince,
        birthDate: membership.personalData.birthDate.split('T')[0],
        role: membership.personalData.role,
        businessName: membership.companyData.businessName,
        vatNumber: membership.companyData.vatNumber,
        fiscalCode: membership.companyData.fiscalCode,
        street: membership.companyData.address.street,
        number: membership.companyData.address.number,
        city: membership.companyData.address.city,
        province: membership.companyData.address.province,
        zipCode: membership.companyData.address.zipCode,
        phone: membership.companyData.contacts.phone || '',
        fax: membership.companyData.contacts.fax || '',
        mobile: membership.companyData.contacts.mobile || '',
        email: membership.companyData.contacts.email,
        website: membership.companyData.contacts.website || '',
        pec: membership.companyData.contacts.pec || '',
        iban: membership.companyData.iban || '',
        mainActivity: membership.companyData.mainActivity,
        hasRepresentative: membership.legalRepresentative?.hasRepresentative || false,
        repFirstName: membership.legalRepresentative?.firstName || '',
        repLastName: membership.legalRepresentative?.lastName || '',
        repBirthPlace: membership.legalRepresentative?.birthPlace || '',
        repBirthProvince: membership.legalRepresentative?.birthProvince || '',
        repBirthDate: membership.legalRepresentative?.birthDate ? membership.legalRepresentative.birthDate.split('T')[0] : '',
        repPosition: membership.legalRepresentative?.position || '',
        repMobile: membership.legalRepresentative?.mobile || ''
      });

      setDeclarations({
        acceptsAssociationPolicy: membership.declarations?.acceptsAssociationPolicy || false,
        acceptsProfessionalConduct: membership.declarations?.acceptsProfessionalConduct || false
      });

      setConsents({
        dataProcessing: membership.privacyConsents.dataProcessing || false,
        mandatory: membership.privacyConsents.mandatory,
        promotionalTraditional: membership.privacyConsents.promotionalTraditional,
        promotionalElectronic: membership.privacyConsents.promotionalElectronic
      });

      // Carica documenti esistenti
      setExistingDocuments({
        identity: membership.documents.identity || null,
        authorization: membership.documents.authorization || null,
        businessRegistry: membership.documents.businessRegistry || null,
        representativeDocument: membership.documents.representativeDocument || null
      });

    } catch (error) {
      console.error('Errore:', error);
      toast.error(error instanceof Error ? error.message : 'Errore nel caricamento');
      setValidToken(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (field: string, file: File | null) => {
    setDocuments(prev => ({ ...prev, [field]: file }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleDeclarationChange = (field: string, value: boolean) => {
    setDeclarations(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleConsentChange = (field: string, value: boolean) => {
    setConsents(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validazione dati personali
    if (!formData.firstName.trim()) newErrors.firstName = 'Campo obbligatorio';
    if (!formData.lastName.trim()) newErrors.lastName = 'Campo obbligatorio';
    if (!formData.birthPlace.trim()) newErrors.birthPlace = 'Campo obbligatorio';
    if (!formData.birthProvince.trim()) newErrors.birthProvince = 'Campo obbligatorio';
    if (!formData.birthDate) newErrors.birthDate = 'Campo obbligatorio';
    if (!formData.role.trim()) newErrors.role = 'Campo obbligatorio';

    // Validazione dati azienda
    if (!formData.businessName.trim()) newErrors.businessName = 'Campo obbligatorio';
    if (!formData.vatNumber.trim()) newErrors.vatNumber = 'Campo obbligatorio';
    if (!formData.fiscalCode.trim()) newErrors.fiscalCode = 'Campo obbligatorio';
    if (!formData.street.trim()) newErrors.street = 'Campo obbligatorio';
    if (!formData.number.trim()) newErrors.number = 'Campo obbligatorio';
    if (!formData.city.trim()) newErrors.city = 'Campo obbligatorio';
    if (!formData.province.trim()) newErrors.province = 'Campo obbligatorio';
    if (!formData.zipCode.trim()) newErrors.zipCode = 'Campo obbligatorio';
    if (!formData.email.trim()) newErrors.email = 'Campo obbligatorio';
    if (!formData.mainActivity.trim()) newErrors.mainActivity = 'Campo obbligatorio';

    // Validazione rappresentante legale
    if (formData.hasRepresentative) {
      if (!formData.repFirstName.trim()) newErrors.repFirstName = 'Campo obbligatorio';
      if (!formData.repLastName.trim()) newErrors.repLastName = 'Campo obbligatorio';
      if (!formData.repBirthPlace.trim()) newErrors.repBirthPlace = 'Campo obbligatorio';
      if (!formData.repBirthProvince.trim()) newErrors.repBirthProvince = 'Campo obbligatorio';
      if (!formData.repBirthDate) newErrors.repBirthDate = 'Campo obbligatorio';
      if (!formData.repPosition.trim()) newErrors.repPosition = 'Campo obbligatorio';
    }

    // Validazione dichiarazioni
    if (!declarations.acceptsAssociationPolicy) {
      newErrors.acceptsAssociationPolicy = 'Devi accettare di sostenere la politica dell\'Associazione';
    }
    if (!declarations.acceptsProfessionalConduct) {
      newErrors.acceptsProfessionalConduct = 'Devi accettare i principi di condotta professionale';
    }

    // Validazione consensi
    if (!consents.dataProcessing) {
      newErrors.dataProcessing = 'Devi accettare il trattamento dei dati obbligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      const errorCount = Object.keys(errors).length;
      toast.error(`Correggi ${errorCount} ${errorCount === 1 ? 'errore' : 'errori'} nel modulo`);
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[class*="border-red"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    try {
      setSubmitting(true);

      const formDataToSend = new FormData();

      // Prepara i dati
      const dataToSend = {
        personalData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          birthPlace: formData.birthPlace,
          birthProvince: formData.birthProvince,
          birthDate: formData.birthDate,
          role: formData.role
        },
        companyData: {
          businessName: formData.businessName,
          vatNumber: formData.vatNumber,
          fiscalCode: formData.fiscalCode,
          address: {
            street: formData.street,
            number: formData.number,
            city: formData.city,
            province: formData.province,
            zipCode: formData.zipCode
          },
          contacts: {
            phone: formData.phone,
            fax: formData.fax,
            mobile: formData.mobile,
            email: formData.email,
            website: formData.website,
            pec: formData.pec
          },
          iban: formData.iban,
          mainActivity: formData.mainActivity
        },
        legalRepresentative: {
          hasRepresentative: formData.hasRepresentative,
          firstName: formData.repFirstName,
          lastName: formData.repLastName,
          birthPlace: formData.repBirthPlace,
          birthProvince: formData.repBirthProvince,
          birthDate: formData.repBirthDate,
          position: formData.repPosition,
          mobile: formData.repMobile
        },
        declarations: declarations,
        privacyConsents: consents
      };

      formDataToSend.append('formData', JSON.stringify(dataToSend));

      // Aggiungi documenti se presenti
      if (documents.identity) formDataToSend.append('identity', documents.identity);
      if (documents.authorization) formDataToSend.append('authorization', documents.authorization);
      if (documents.businessRegistry) formDataToSend.append('businessRegistry', documents.businessRegistry);
      if (documents.representativeDocument) formDataToSend.append('representativeDocument', documents.representativeDocument);

      console.log('Invio aggiornamento domanda...');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/memberships/edit/${token}`,
        {
          method: 'PUT',
          body: formDataToSend
        }
      );

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(data.message || 'Errore durante l\'aggiornamento');
      }

      setShowSuccessModal(true);

    } catch (error) {
      console.error('Errore:', error);
      toast.error(error instanceof Error ? error.message : 'Errore durante l\'aggiornamento');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Link Non Valido
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Il link di modifica è scaduto o non è valido. Contatta l'amministrazione per richiedere un nuovo link.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Modale Successo */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all">
            <div className="text-center">
              {/* Icona Successo */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                <svg className="h-10 w-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Titolo */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Domanda Aggiornata!
              </h3>

              {/* Messaggio */}
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                La tua domanda di associazione è stata aggiornata con successo. Riceverai una conferma via email.
              </p>

              {/* Pulsante */}
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
              >
                Torna alla Home
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Modifica Domanda di Iscrizione
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Modifica i dati della tua domanda di iscrizione. Dopo l'invio, la domanda tornerà in stato "In Attesa" per la verifica.
            </p>
          </div>

        <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-8">
          <PersonalDataSection
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
          />

          <CompanyDataSection
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
          />

          <LegalRepresentativeSection
            formData={formData}
            errors={errors}
            onChange={handleFieldChange}
          />

          <DeclarationSection
            formData={declarations}
            errors={errors}
            onChange={handleDeclarationChange}
          />

          <DocumentsUploadSection
            documents={documents}
            errors={errors}
            onFileChange={handleFileChange}
            existingDocuments={existingDocuments}
            editToken={token}
          />

          <PrivacyConsentsSection
            consents={consents}
            errors={errors}
            onChange={handleConsentChange}
          />

          {/* Submit Button */}
          <div className="flex justify-center gap-4 pt-6">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors text-lg"
            >
              Annulla
            </button>
            <LoadingButton
              type="submit"
              loading={submitting}
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors text-lg"
            >
              Aggiorna Domanda
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default EditMembershipPage;

